/**
 * Where today sits in the athlete's own plan: the runway, the current block, and fitness against
 * the target path. Pure; no React. The plan is athlete-written, so this only measures, never plans.
 */
import type { FitnessPoint, PlanBlock, TrainingPlan } from '@gd/shared'
import { addDays, daysBetween } from '@gd/shared/dates'

/** How far back plan views read fitness when the plan started long ago. */
const MAX_HISTORY_DAYS = 365

/**
 * The fitness window plan views read: from the day before the plan started (capped at a year).
 * Shared so the Plan page and Today hit the same cached query.
 */
export function planFitnessFrom(plan: TrainingPlan, today: string): string {
  const from = [plan.start, addDays(today, -MAX_HISTORY_DAYS)].sort().at(-1)!
  return addDays(from, -1)
}

/** Fitness within this many points of the target path reads as on track. */
export const ON_TRACK_MARGIN = 3

export interface PlanRunway {
  /** 1-based week of the plan today falls in. */
  week: number
  /** Total weeks to the goal date; null for an open-ended plan. */
  totalWeeks: number | null
  /** Whole weeks left to the goal date; null for an open-ended plan. */
  weeksToGo: number | null
  /** Days to the goal date; null for an open-ended plan. */
  daysToGo: number | null
  /** True before the plan's start date. */
  notStarted: boolean
}

export function planRunway(plan: TrainingPlan, today: string): PlanRunway {
  const sinceStart = daysBetween(plan.start, today)
  const week = Math.max(1, Math.floor(sinceStart / 7) + 1)
  if (!plan.goalDate) {
    return { week, totalWeeks: null, weeksToGo: null, daysToGo: null, notStarted: sinceStart < 0 }
  }
  const totalDays = daysBetween(plan.start, plan.goalDate)
  const daysToGo = Math.max(0, daysBetween(today, plan.goalDate))
  return {
    week,
    totalWeeks: Math.max(1, Math.ceil((totalDays + 1) / 7)),
    weeksToGo: Math.floor(daysToGo / 7),
    daysToGo,
    notStarted: sinceStart < 0,
  }
}

export interface CurrentBlock {
  block: PlanBlock
  index: number
  /** 1-based week within the block. */
  week: number
  weeks: number
}

/** The block today falls in, or null between blocks or outside them. */
export function currentBlock(plan: TrainingPlan, today: string): CurrentBlock | null {
  const index = plan.blocks.findIndex((b) => b.start <= today && today <= b.end)
  if (index < 0) return null
  const block = plan.blocks[index]!
  return {
    block,
    index,
    week: Math.floor(daysBetween(block.start, today) / 7) + 1,
    weeks: Math.max(1, Math.ceil((daysBetween(block.start, block.end) + 1) / 7)),
  }
}

/** The next block that has not started yet, if any. */
export function nextBlock(plan: TrainingPlan, today: string): PlanBlock | null {
  return plan.blocks.find((b) => b.start > today) ?? null
}

export interface TargetWaypoint {
  date: string
  ctl: number
}

/**
 * The target path: fitness at the plan's start, each block's end target, then the plan's own
 * target at the goal date (or the last block's end). Null when nothing sets a target.
 */
export function targetWaypoints(
  plan: TrainingPlan,
  points: readonly FitnessPoint[],
): TargetWaypoint[] | null {
  const targets: TargetWaypoint[] = plan.blocks
    .filter((b) => b.fitnessTarget != null)
    .map((b) => ({ date: b.end, ctl: b.fitnessTarget! }))
  if (plan.fitnessTarget != null) {
    const end = plan.goalDate ?? plan.blocks.at(-1)?.end ?? null
    if (end && !targets.some((t) => t.date === end))
      targets.push({ date: end, ctl: plan.fitnessTarget })
  }
  if (targets.length === 0) return null
  const startCtl = fitnessOn(points, plan.start)
  const path = startCtl == null ? [] : [{ date: plan.start, ctl: startCtl }]
  return [...path, ...targets.filter((t) => t.date > plan.start)].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
  )
}

/** Fitness on a date: the point on that date, else the nearest earlier one, else the first. */
function fitnessOn(points: readonly FitnessPoint[], date: string): number | null {
  let found: number | null = null
  for (const p of points) {
    if (p.ctl == null) continue
    if (p.date <= date) found = p.ctl
    else return found ?? p.ctl
  }
  return found
}

/** The target fitness on a date, interpolated along the waypoints; null outside them. */
export function targetOn(waypoints: readonly TargetWaypoint[], date: string): number | null {
  if (waypoints.length === 0) return null
  if (waypoints.length === 1) return date <= waypoints[0]!.date ? waypoints[0]!.ctl : null
  for (let i = 1; i < waypoints.length; i++) {
    const a = waypoints[i - 1]!
    const b = waypoints[i]!
    if (date >= a.date && date <= b.date) {
      const span = daysBetween(a.date, b.date)
      const t = span === 0 ? 1 : daysBetween(a.date, date) / span
      return a.ctl + (b.ctl - a.ctl) * t
    }
  }
  return null
}

export type TargetStatus = 'ahead' | 'on-track' | 'behind'

export interface TargetCheck {
  target: number
  actual: number
  gap: number
  status: TargetStatus
  /** The next waypoint after today: what fitness is heading for, and by when. */
  next: TargetWaypoint | null
}

/** Today's fitness against the target path. Null when the plan sets no target or there is no data. */
export function checkTarget(
  plan: TrainingPlan,
  points: readonly FitnessPoint[],
  today: string,
): TargetCheck | null {
  const waypoints = targetWaypoints(plan, points)
  const actual = points.at(-1)?.ctl
  if (!waypoints || actual == null) return null
  const target = targetOn(waypoints, today)
  if (target == null) return null
  const gap = actual - target
  return {
    target,
    actual,
    gap,
    status: gap > ON_TRACK_MARGIN ? 'ahead' : gap < -ON_TRACK_MARGIN ? 'behind' : 'on-track',
    next: waypoints.find((w) => w.date > today) ?? null,
  }
}

export const TARGET_STATUS_LABEL: Record<TargetStatus, string> = {
  ahead: 'Ahead of plan',
  'on-track': 'On track',
  behind: 'Behind plan',
}
